import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { debounce } from '@repo/utils';
import { useEffect, useRef } from 'react';

type Props = {
  onDirtyChange: (isDirty: boolean) => void;
  initialValue?: string;
};

const debouncedCheck = debounce(
  (
    currentState: string,
    initialState: string | null,
    onDirtyChange: Props['onDirtyChange'],
  ) => {
    if (!initialState) return onDirtyChange(false);

    const isDirty = currentState !== initialState;
    onDirtyChange(isDirty);
  },
  500,
);

export default function OnDirtyPlugin({ onDirtyChange, initialValue }: Props) {
  const [editor] = useLexicalComposerContext();
  const initialStateRef = useRef<string | null>(null);

  // Initialize ref with actual loaded state on mount
  useEffect(() => {
    if (initialStateRef.current === null) {
      const loadedState = JSON.stringify(editor.getEditorState().toJSON());
      initialStateRef.current = initialValue ?? loadedState;

      // Check immediately if loaded state differs from DB
      if (initialValue && loadedState !== initialValue) onDirtyChange(true);
    }
  }, [editor, initialValue, onDirtyChange]);

  // Update ref when initialValue changes (after submission)
  useEffect(() => {
    if (initialValue && initialStateRef.current !== null) {
      initialStateRef.current = initialValue;
      const currentState = JSON.stringify(editor.getEditorState().toJSON());
      debouncedCheck(currentState, initialValue, onDirtyChange);
    }
  }, [initialValue, editor, onDirtyChange]);

  // Listen for editor changes
  useEffect(() => {
    const removeListener = editor.registerUpdateListener(({ editorState }) => {
      const currentState = JSON.stringify(editorState.toJSON());
      debouncedCheck(currentState, initialStateRef.current, onDirtyChange);
    });
    return () => removeListener();
  }, [editor, onDirtyChange]);

  return null;
}
